/**
 * @file VoiceEnrollmentPage.tsx
 * @brief "Register your voice" on the web and in the desktop app: the phone's
 *        voice enrollment, same script, same sample.
 *
 * @details
 * DES 04-10-2026: voice enrollment was phone-only, so a desktop-only user
 * could never be recognised by Meeting Intel. Mobile is the reference
 * (FRONTEND VoiceEnrollmentScreen.tsx):
 *
 * - Enrolled: "Voice registered", play the sample, record a new one, or
 *   remove it (any user, corporate too: it is their biometric data, not a
 *   company record — DES chose this 04-10-2026).
 * - Prepare: the script preview, the microphone, and a consent switch that
 *   must be on before recording (GDPR Art. 9, voice is biometric data).
 * - 3-2-1, then ONE recording: three paragraphs to read (40s), then free
 *   speech in the user's own language (21s). Stops on its own at 61s.
 * - Review: listen, re-record, or save.
 *
 * The mic is the Boardroom's raw profile (captureProfile.ts): the voiceprint
 * is matched against Boardroom audio, so both are captured the same way.
 *
 * @references
 * - api.ts, enrollmentScript.ts
 * - FRONTEND/src/screens/VoiceEnrollment/VoiceEnrollmentScreen.tsx
 *
 * @author DES
 * @date 04-10-2026
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CircleCheck,
  CirclePlay,
  LoaderCircle,
  Mic,
  RotateCcw,
  Square,
  Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { FormFieldGroup } from "@/components/ui/FormSheet";
import { DockActionButton, PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { Switch } from "@/components/ui/Switch";
import { MicPicker, describeMicInUse } from "@/features/employee-boardroom/MicPicker";
import { useBoardroomBasePath } from "@/features/employee-boardroom/useBoardroomBasePath";
import { boardroomAudioConstraints } from "@/features/employee-boardroom/capture/captureProfile";
import { describeMicError, describeRecorderSupportError } from "@/features/employee-boardroom/capture/micErrors";
import { startMicMeter, type MicMeterHandle } from "@/features/employee-boardroom/capture/micMeter";
import { pickPreferredMime } from "@/features/employee-boardroom/capture/preferredMime";
import { useMicDevices } from "@/features/employee-boardroom/capture/useMicDevices";
import { useAuth } from "@/shared/auth/auth-context";
import { useDesktopDefaultMic } from "@/shared/desktop/useDesktopSettings";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { useRemoveVoiceSample, useSaveVoiceSample, useVoiceConsent, useVoiceEnrollment } from "./api";
import { ApiClientError } from "@/shared/api/client";
import {
  ENROLLMENT_MAX_SECONDS,
  ENROLLMENT_MIN_SECONDS,
  FREE_SPEECH_HINT,
  FREE_SPEECH_NOTE,
  buildEnrollmentCues,
  resolveCueState,
  splitEmphasis,
  totalCueSeconds,
  type EnrollmentCue,
  type EnrollmentCueState,
} from "./enrollmentScript";

type Phase = "prepare" | "countdown" | "recording" | "review" | "saving";

type Take = {
  blob: Blob;
  url: string;
  contentType: string;
  extension: string;
  durationSec: number;
};

function clock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** One <audio> for every play button on the page. */
function usePlayer() {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const stop = useCallback(() => {
    audio.current?.pause();
    audio.current = null;
    setPlaying(null);
  }, []);
  const toggle = useCallback(
    (src: string) => {
      if (playing === src) return stop();
      stop();
      const a = new Audio(src);
      audio.current = a;
      a.onended = () => setPlaying(null);
      a.onerror = () => {
        setPlaying(null);
        toast({ variant: "destructive", title: "Couldn't play the sample" });
      };
      setPlaying(src);
      void a.play().catch(() => setPlaying(null));
    },
    [playing, stop],
  );
  useEffect(() => stop, [stop]);
  return { playing, toggle, stop };
}

function Teleprompter({
  cues,
  state,
  live,
}: {
  cues: EnrollmentCue[];
  state: EnrollmentCueState | null;
  live: boolean;
}) {
  const index = live && state ? state.index : 0;
  const cue = cues[index]!;
  const readCount = cues.filter((c) => c.kind === "read").length;
  const free = cue.kind === "free_speech";
  const progress = live && state ? state.progress : 0;

  return (
    <div className="rounded-[28px] bg-app-text/[0.04] px-6 py-6 md:px-8 md:py-7">
      <div className="flex items-center justify-between gap-3 text-xs font-medium uppercase tracking-widest text-muted-foreground">
        <span className="shrink-0">{!live ? "Script preview" : free ? "Your turn" : "Read aloud"}</span>
        <span className="text-right">
          {!live ? `${readCount} paragraphs + free speech` : free ? "Free speech" : `${index + 1} / ${readCount}`}
        </span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-app-text/10">
          <div
            className="h-full rounded-full bg-brand transition-[width] duration-300 ease-linear"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
        <span className={cn("w-9 text-right text-xs tabular-nums text-muted-foreground", !live && "opacity-0")}>
          {Math.round(progress * 100)}%
        </span>
      </div>
      {/* Fixed height: the card never jumps between paragraphs. */}
      <div key={cue.id} className="mt-5 flex min-h-[9.5rem] items-center animate-in fade-in duration-300">
        {free ? (
          <div>
            <p className="text-2xl font-semibold leading-snug md:text-[28px]">{FREE_SPEECH_HINT}</p>
            <p className="mt-2 text-sm text-muted-foreground">{FREE_SPEECH_NOTE}</p>
          </div>
        ) : (
          <p className="text-2xl leading-snug md:text-[28px]">
            {splitEmphasis(cue.text, cue.emphasisWords).map((part, i) =>
              part.emphasis ? (
                <span key={i} className="font-semibold text-brand">
                  {part.text}
                </span>
              ) : (
                <span key={i}>{part.text}</span>
              ),
            )}
          </p>
        )}
      </div>
    </div>
  );
}

/** DEV preview only (`/__preview/voice-enrollment?state=saving`): open held
 *  in the saving state with a stand-in 64s take; nothing is sent. */
const PREVIEW_SAVING_TAKE: Take = {
  blob: new Blob(),
  url: "",
  contentType: "audio/webm",
  extension: "webm",
  durationSec: 64,
};

export function VoiceEnrollmentPage({ previewSaving = false }: { previewSaving?: boolean } = {}) {
  const navigate = useNavigate();
  const base = useBoardroomBasePath();
  const { user } = useAuth();
  const enrollmentQuery = useVoiceEnrollment();
  const save = useSaveVoiceSample();
  const remove = useRemoveVoiceSample();
  const consentRecord = useVoiceConsent(enrollmentQuery.data?.status === "enrolled");
  const player = usePlayer();

  const cues = useMemo(
    () => buildEnrollmentCues(user?.firstName ?? "", user?.lastName ?? ""),
    [user?.firstName, user?.lastName],
  );
  const total = totalCueSeconds(cues);

  const [phase, setPhase] = useState<Phase>(previewSaving ? "saving" : "prepare");
  const [reRecording, setReRecording] = useState(false);
  const [consent, setConsent] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const [take, setTake] = useState<Take | null>(previewSaving ? PREVIEW_SAVING_TAKE : null);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const [deviceId, setDeviceId] = useState("");
  const { devices, error: micListError, loading: micsLoading } = useMicDevices({ hideVerblizrMic: true });
  useDesktopDefaultMic(devices, deviceId, setDeviceId);

  // Live capture, outside React state.
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const timer = useRef<number | null>(null);
  const meter = useRef<MicMeterHandle | null>(null);
  const level = useRef(0);
  const glow = useRef<HTMLDivElement | null>(null);
  const discard = useRef(false);

  const release = useCallback(() => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = null;
    meter.current?.stop();
    meter.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  }, []);

  // Leaving the page mid-recording throws the take away and frees the mic.
  useEffect(
    () => () => {
      discard.current = true;
      if (recorder.current?.state === "recording") recorder.current.stop();
      release();
    },
    [release],
  );

  useEffect(() => () => {
    if (take) URL.revokeObjectURL(take.url);
  }, [take]);

  // The mic glow follows the live level.
  useEffect(() => {
    if (phase !== "recording" && phase !== "countdown") return;
    let raf = 0;
    let smooth = 0;
    const tick = () => {
      const target = level.current / 100;
      smooth += (target - smooth) * (target > smooth ? 0.6 : 0.2);
      if (glow.current) {
        glow.current.style.transform = `scale(${1 + smooth * 0.45})`;
        glow.current.style.opacity = String(0.12 + smooth * 0.3);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  const finish = useCallback(() => {
    const rec = recorder.current;
    if (rec && rec.state === "recording") rec.stop();
  }, []);

  const beginRecording = useCallback(() => {
    const s = stream.current;
    if (!s) return;
    const mime = pickPreferredMime();
    let rec: MediaRecorder;
    try {
      rec = mime.mimeType ? new MediaRecorder(s, { mimeType: mime.mimeType }) : new MediaRecorder(s);
    } catch (err) {
      release();
      setPhase("prepare");
      setError(describeMicError(err));
      return;
    }
    chunks.current = [];
    discard.current = false;
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.current.push(e.data);
    };
    rec.onstop = () => {
      const durationSec = Math.floor((performance.now() - startedAt.current) / 1000);
      release();
      recorder.current = null;
      if (discard.current) return;
      const contentType = rec.mimeType ? rec.mimeType.split(";")[0]! : mime.contentType;
      const blob = new Blob(chunks.current, { type: contentType });
      if (durationSec < ENROLLMENT_MIN_SECONDS) {
        setPhase("prepare");
        setError(`Please follow the text to the end (about ${ENROLLMENT_MIN_SECONDS} seconds).`);
        return;
      }
      if (blob.size < 1000) {
        setPhase("prepare");
        setError("The recording looks empty. Check the microphone and try again.");
        return;
      }
      setTake({
        blob,
        url: URL.createObjectURL(blob),
        contentType,
        extension: contentType.includes("mp4") ? "m4a" : contentType.includes("ogg") ? "ogg" : "webm",
        durationSec: Math.min(durationSec, ENROLLMENT_MAX_SECONDS),
      });
      setPhase("review");
    };
    recorder.current = rec;
    rec.start(1000);
    startedAt.current = performance.now();
    setElapsed(0);
    setPhase("recording");
    timer.current = window.setInterval(() => {
      const sec = (performance.now() - startedAt.current) / 1000;
      setElapsed(sec);
      // Stops on its own when the script ends, as on the phone.
      if (sec >= total) finish();
    }, 200);
  }, [finish, release, total]);

  // 3-2-1 with the mic already open, so a permission prompt never lands
  // mid-script.
  useEffect(() => {
    if (phase !== "countdown") return;
    if (countdown <= 0) {
      beginRecording();
      return;
    }
    const t = window.setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [phase, countdown, beginRecording]);

  const onReady = async () => {
    setError(null);
    const unsupported = describeRecorderSupportError();
    if (unsupported) {
      setError(unsupported);
      return;
    }
    player.stop();
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        audio: boardroomAudioConstraints(deviceId || undefined),
      });
      stream.current = s;
      meter.current = startMicMeter(s, level);
      setCountdown(3);
      setPhase("countdown");
    } catch (err) {
      release();
      setError(describeMicError(err));
    }
  };

  const onCancelRecording = () => {
    discard.current = true;
    if (recorder.current?.state === "recording") recorder.current.stop();
    release();
    setPhase("prepare");
  };

  const onReRecord = () => {
    player.stop();
    setTake(null);
    setPhase("prepare");
  };

  const onSave = async () => {
    if (!take) return;
    player.stop();
    setPhase("saving");
    try {
      await save.mutateAsync({
        blob: take.blob,
        contentType: take.contentType,
        extension: take.extension,
        durationSeconds: take.durationSec,
      });
      setTake(null);
      setReRecording(false);
      setPhase("prepare");
      toast({ variant: "success", title: "Voice registered" });
    } catch (err) {
      // ADR-046: no consent, nothing kept. Back to the switch, not the take.
      if (err instanceof ApiClientError && err.code === "VOICE_CONSENT_REQUIRED") {
        setTake(null);
        setConsent(false);
        setPhase("prepare");
        toast({ variant: "destructive", title: "Turn on voice matching", description: err.message });
        return;
      }
      setPhase("review");
      toast({
        variant: "destructive",
        title: "Save failed",
        description: err instanceof Error ? err.message : "Please try again.",
      });
    }
  };

  const onRemove = async () => {
    try {
      player.stop();
      await remove.mutateAsync();
      setConfirmRemove(false);
      toast({ variant: "success", title: "Voice removed", description: "Deleted now. Consent withdrawn." });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn't remove your voice",
        description: err instanceof Error ? err.message : "Please try again.",
      });
    }
  };

  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(base);
  };

  const enrollment = enrollmentQuery.data;
  const enrolled = enrollment?.status === "enrolled";
  const showEnrolled = enrolled && !reRecording && phase === "prepare";
  const live = phase === "recording";
  const cueState = live ? resolveCueState(elapsed, cues) : null;

  let hint = "Read three short paragraphs, then speak in your language for a few sentences.";
  if (live && cueState) {
    hint =
      cueState.cue.kind === "free_speech"
        ? `Keep talking. It stops on its own in ${Math.max(0, Math.ceil(total - elapsed))}s.`
        : `Next paragraph in ~${cueState.cueRemainingSec}s.`;
  }

  return (
    <div className={cn(pageWithDockClass, "mx-auto w-full max-w-3xl px-4 pt-6 md:px-5 lg:pt-10")}>
      <div>
        <button
          type="button"
          onClick={goBack}
          className="-ml-1 inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-xs font-medium uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Back
        </button>
        <h1 className="mt-4 text-2xl font-semibold tracking-tight">Register your voice</h1>
        <p className="mt-1 max-w-xl text-sm text-muted-foreground">
          About a minute, once. Meeting Intel then knows which lines in a meeting are yours.
        </p>
      </div>

      {enrollmentQuery.isLoading ? (
        <div className="flex flex-1 items-center justify-center py-24">
          <LoaderCircle className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : showEnrolled && enrollment ? (
        <div className="mt-8 rounded-[28px] bg-app-text/[0.04] px-6 py-6 md:px-8">
          <div className="flex items-start gap-4">
            <CircleCheck className="h-11 w-11 shrink-0 text-brand" strokeWidth={1.75} />
            <div className="min-w-0">
              <p className="text-lg font-semibold">Voice registered</p>
              <p className="text-sm text-muted-foreground">
                {enrollment.durationSeconds
                  ? `${enrollment.durationSeconds}s sample saved for Meeting Intel`
                  : "Your sample is ready for Meeting Intel"}
                {enrollment.enrolledAt
                  ? ` · Updated ${new Date(enrollment.enrolledAt).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}`
                  : ""}
              </p>
              {consentRecord.data?.given && consentRecord.data.at ? (
                <p className="text-xs text-muted-foreground">
                  Consent given on{" "}
                  {new Date(consentRecord.data.at).toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              ) : null}
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {enrollment.playbackUrl ? (
              <button
                type="button"
                onClick={() => player.toggle(enrollment.playbackUrl!)}
                className="inline-flex h-10 items-center gap-2 rounded-full bg-app-text/[0.06] px-4 text-sm font-medium hover:bg-app-text/10"
              >
                {player.playing === enrollment.playbackUrl ? (
                  <Square className="h-4 w-4 fill-current text-brand" />
                ) : (
                  <CirclePlay className="h-4 w-4 text-brand" />
                )}
                {player.playing === enrollment.playbackUrl ? "Stop playback" : "Play my sample"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setConfirmRemove(true)}
              className="inline-flex h-10 items-center gap-2 rounded-full px-3 text-sm font-medium text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Remove my voice and withdraw consent
            </button>
          </div>
        </div>
      ) : phase === "review" || phase === "saving" ? (
        take ? (
          <div className="mt-8 flex flex-col items-center rounded-[28px] bg-app-text/[0.04] px-6 py-10 text-center">
            <button
              type="button"
              onClick={() => player.toggle(take.url)}
              disabled={phase === "saving"}
              aria-label={player.playing === take.url ? "Stop" : "Play your recording"}
              className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-brand/90 disabled:opacity-60"
            >
              {player.playing === take.url ? (
                <Square className="h-7 w-7 fill-current" />
              ) : (
                <CirclePlay className="h-9 w-9" strokeWidth={1.5} />
              )}
            </button>
            <p className="mt-4 text-lg font-semibold">
              {phase === "saving" ? "Saving your voice sample…" : "Review your recording"}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {/* While saving: keep the page open (DES 14-10-2026, user
                  feedback on the phone: people closed the app mid-save). */}
              {phase === "saving"
                ? "Keep this page open until your voice is saved."
                : `${clock(take.durationSec)} · Listen once, then save. Re-record if it sounds noisy or cut off.`}
            </p>
          </div>
        ) : null
      ) : (
        <div className="mt-8">
          <Teleprompter cues={cues} state={cueState} live={live} />
          <p className="mt-3 min-h-[1.25rem] text-center text-sm text-muted-foreground">{hint}</p>

          {phase === "countdown" || live ? (
            <div className="mt-6 flex flex-col items-center">
              <div className="relative flex h-24 w-24 items-center justify-center">
                <div ref={glow} className="absolute inset-0 rounded-full bg-brand opacity-10" aria-hidden />
                <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-brand text-white">
                  {phase === "countdown" ? (
                    <span className="text-2xl font-semibold tabular-nums">{countdown}</span>
                  ) : (
                    <Mic className="h-7 w-7" strokeWidth={1.5} />
                  )}
                </div>
              </div>
              <p className="mt-3 font-mono text-sm tabular-nums text-muted-foreground">
                {phase === "countdown" ? "Get ready" : `${clock(elapsed)} / ${clock(total)}`}
              </p>
            </div>
          ) : (
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <FormFieldGroup label="Microphone" hint={describeMicInUse(devices, deviceId, micsLoading)}>
                <MicPicker
                  compact
                  devices={devices}
                  value={deviceId}
                  onChange={setDeviceId}
                  loading={micsLoading}
                  error={null}
                />
              </FormFieldGroup>
              <label className="flex items-start justify-between gap-3 border-b border-app-text/10 pb-2.5">
                <span className="min-w-0">
                  <span className="block text-[15px] font-medium">I agree to voice matching</span>
                  <span className="block text-xs leading-relaxed text-muted-foreground">
                    Verblizr keeps this sample and a voiceprint made from it, only to recognise your voice in
                    your meetings. Remove it any time from this page.
                  </span>
                </span>
                <Switch checked={consent} onCheckedChange={setConsent} aria-label="I agree to voice matching" />
              </label>
            </div>
          )}

          {error || (phase === "prepare" && micListError) ? (
            <p className="mt-4 text-sm text-destructive" role="alert">
              {error || micListError}
            </p>
          ) : null}
        </div>
      )}

      {!enrollmentQuery.isLoading ? (
        <PageDock>
          {showEnrolled ? (
            <DockActionButton icon={<Mic className="h-5 w-5" />} onClick={() => setReRecording(true)}>
              Record a new sample
            </DockActionButton>
          ) : phase === "review" || phase === "saving" ? (
            <>
              <button
                type="button"
                onClick={onReRecord}
                disabled={phase === "saving"}
                className="inline-flex h-14 items-center gap-2 px-4 text-base font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4" />
                Re-record
              </button>
              <DockActionButton
                onClick={onSave}
                disabled={phase === "saving"}
                icon={phase === "saving" ? <LoaderCircle className="h-5 w-5 animate-spin" /> : undefined}
              >
                {phase === "saving" ? "Saving…" : "Save voice sample"}
              </DockActionButton>
            </>
          ) : phase === "countdown" || live ? (
            <button
              type="button"
              onClick={onCancelRecording}
              className="inline-flex h-14 items-center px-4 text-base font-medium text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          ) : (
            <>
              {enrolled ? (
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setReRecording(false);
                  }}
                  className="inline-flex h-14 items-center px-4 text-base font-medium text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              ) : null}
              <DockActionButton
                icon={<Mic className="h-5 w-5" />}
                onClick={() => void onReady()}
                disabled={!consent}
                title={consent ? undefined : "Turn on “I agree to voice matching” first"}
              >
                I&apos;m ready
              </DockActionButton>
            </>
          )}
        </PageDock>
      ) : null}

      <AlertDialog open={confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove your voice and withdraw consent?</AlertDialogTitle>
            <AlertDialogDescription>
              Your sample and voiceprint are deleted now, and your consent is withdrawn. Meeting Intel stops
              recognising you until you register again. Meetings already processed keep their speaker names.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void onRemove();
              }}
              disabled={remove.isPending}
            >
              {remove.isPending ? "Removing…" : "Remove and withdraw"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default VoiceEnrollmentPage;
